import React, { useCallback, useReducer, useState } from 'react';
import MenuModal from './Menu-Modal';
import { Button, LinearProgress } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import './Menu-Create.scss';
import {
  IN_ROOM_DINING_MENU_CREATE,
  IRD_MENU_BUILD,
  IRD_MENU_IMPORT,
  IRD_MENU_UPLOAD,
} from '../../../../../constants/InRoomDiningConst';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllInRoomDiningMenu, getMenuFormData, setMenuType } from '../../../servicesSlice';
import { addInRoomDiningMenu, getInRoomDiningPosMenu } from '../../../../../utils/ServicesAPIUtils';
import InfoIcon from '@mui/icons-material/Info';
import { useDropzone } from 'react-dropzone';
import { getPreSignedUrl } from '../../../../../utils/PropertyAPIUtils';
import { selectedGroupSelector } from '../../../../property/propertySlice';
import CancelIcon from '@mui/icons-material/Cancel';
import { bytesToSize, convertFileToBase64 } from '../../../../../utils/commonUtils';
import { scanPdfSecurity } from '../../../../../utils/pdfSecurityUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { v4 as uuidv4 } from 'uuid';
import MenuFileUpload from './Menu-File-Upload';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import { ENVIRONMENT } from '../../../../../env/Configurations';

const InRoomDiningMenuCreateType = ({
  onViewToggle,
  hotelId,
  restaurantId,
  showLoader = () => {},
}) => {
  const [menuModal, setMenuModal] = useState(false);
  const [posMenuList, setPosMenuList] = useState([]);
  const [upload, setUpload] = useState(false);
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(0);
  const [acceptedFile, setAcceptedFile] = useState(null);
  const [fileDetails, setFileDetails] = useState(null);
  const [uploadError, setUploadError] = useState(false);
  const [menuCreateOption, setMenuCreateOption] = useState(null);
  const [isUploadDoc, setIsUploadDoc] = useState(false);

  const onDropAccepted = useCallback((acceptedFiles) => {
    setUpload(true);
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleDroppredFile(acceptedFiles[0]);
    }
  }, []);

  const selectedGroup = useSelector(selectedGroupSelector);
  const { getRootProps, getInputProps, open } = useDropzone({
    accept: 'application/pdf',
    noClick: true,
    multiple: false,
    onDropAccepted,
  });
  const [documentType, setDocumentType] = useState('S3');
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    menuUpload: {
      value: { url: '', type: '' },
      fieldName: 'Menu Upload',
      errors: {},
      validations: ['required'],
      tab: 2,
    },
  });

  const [document, setDocument] = useState({
    url: formData?.menuUpload?.value?.url,
    type: formData?.menuUpload?.value?.type,
  });

  const onClickScratch = () => {
    dispatch(
      getMenuFormData({
        name: 'New Menu 01',
        code: '',
        description: '',
        isActive: true,
        hours: [],
        categories: [],
        images: [],
        hotelId,
      }),
    );
    onViewToggle(IN_ROOM_DINING_MENU_CREATE, true, null);
  };

  const onClickImportMenu = async () => {
    try {
      dispatch(stepUpLoaderCount());
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      const posMenu = await getInRoomDiningPosMenu(hotelId, language);
      setPosMenuList(posMenu.data.getIRDMenuStatus);
      setMenuModal(true);
    } catch (error) {
      if (ENVIRONMENT === 'dev') {
        console.log(error.message);
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const onDocDelete = () => {
    setAcceptedFile(null);
    setUploadError(false);
  };

  const handleDroppredFile = async (fileObj) => {
    setAcceptedFile(fileObj);
    setFileDetails(fileObj);
    setProgress(0);
    if (fileObj.size > 50000000) {
      setUploadError(true);
      setUpload(false);
      notificationManager('danger', 'File size larger than 50MB');
      return;
    }

    try {
      await scanPdfSecurity(fileObj);
    } catch (securityError) {
      setUpload(false);
      setUploadError(true);
      notificationManager('danger', securityError?.message || 'Upload blocked: Unsafe PDF content detected.');
      return;
    }

    if (fileObj) {
      convertFileToBase64(fileObj)
        .then(async (base64) => {
          const input = {
            groupId: selectedGroup,
            propertyType: 'hotels',
            type: 'docs',
            contentLength: fileObj.size,
            contentType: fileObj.type,
            filename: fileObj.name,
            skipContentValidation: false,
            isDocUpload: true,
            contents: base64,
          };

          try {
            const res = await getPreSignedUrl(input);
            setProgress(50);

            if (res?.data?.getPreSignUrlCMS?.key) {
              setProgress(100);
              setDocument({
                url: res.data.getPreSignUrlCMS.key,
                type: documentType,
              });
              setAcceptedFile(null);
              await createIrdMenu(fileObj.name, res.data.getPreSignUrlCMS.key);
            } else {
              setUploadError(true);
              setUpload(false);
              notificationManager('danger', 'Error uploading file');
              return;
            }
          } catch (exception) {
            setUploadError(true);
            setUpload(false);

            if (ENVIRONMENT === 'dev') {
              console.log(exception?.message);
            }

            if (exception?.message) {
              notificationManager('danger', exception?.message.replace('GraphQL error:', ''));
            } else {
              notificationManager('danger', 'Error while uploading file.');
            }
          }
        })
        .catch((error) => {
          if (ENVIRONMENT === 'dev') {
            console.log(error?.message, 'error');
          }

          notificationManager('danger', 'Error converting file to base64');
        });
    }
  };

  const createIrdMenu = async (name, url, images = []) => {
    const selectedLanguage = decryptLocalStorage('lang');
    const language = selectedLanguage !== 'en' ? selectedLanguage : '';
    const payload = {
      hotelId: hotelId,
      restaurantId: restaurantId,
      menuURL: url,
      menuType: menuCreateOption,
      code: uuidv4().split('-')[0],
      isActive: true,
      name: name.split('.')[0],
      images,
    };

    try {
      dispatch(stepUpLoaderCount());
      await addInRoomDiningMenu(payload, language);
      dispatch(fetchAllInRoomDiningMenu(hotelId, restaurantId));
      notificationManager('success', `Menu uploaded successfully`);
    } catch (error) {
      if (ENVIRONMENT === 'dev') {
        console.log(error?.message, 'error');
      }

      error?.graphQLErrors
        ? notificationManager('danger', error?.graphQLErrors?.[0]?.message)
        : notificationManager('danger', 'Error uploading file');
      setUploadError(true);
      setUpload(false);
    } finally {
      dispatch(stepDownLoaderCount());
      onViewToggle(false, null);
    }
  };

  const onConfirm = () => {
    dispatch(setMenuType(menuCreateOption));
    if (menuCreateOption === IRD_MENU_IMPORT) {
      onClickImportMenu();
    } else if (menuCreateOption === IRD_MENU_BUILD) {
      onClickScratch();
    } else {
      setIsUploadDoc(true);
    }
  };

  return (
    <div className='container mt-4 mb-3 ird-detail-view pb-4'>
      <div className='col-md-6 px-0 d-flex align-items-center'>
        <Button
          variant='text'
          color='secondary'
          size='small'
          startIcon={<ArrowBack />}
          onClick={(e) => {
            isUploadDoc ? setIsUploadDoc(false) : onViewToggle(false, null);
          }}
          className='me-2'
        >
          Back
        </Button>
        <span className='page-title'>{!isUploadDoc ? 'Create Menu' : 'Upload Menu'}</span>
      </div>
      {!isUploadDoc && (
        <div className='card ird-card shadow mx-5 mt-3'>
          <div className='mt-5 row justify-content-center mx-0'>
            {upload && (
              <div className='d-flex justify-content-center align-items-center progress-bar-screen'>
                <div className='upload-progress p-3 d-flex justify-content-around'>
                  <div>{fileDetails?.name}</div>
                  <div>{fileDetails?.size && bytesToSize(fileDetails?.size)}</div>
                  <div className=' upload-progress-title'>Uploading</div>
                  <div className=' mt-2 w-25'>
                    <LinearProgress variant='determinate' color='secondary' value={progress} />
                  </div>
                  <CancelIcon
                    className=' pointer me-1 '
                    onClick={(e) => onDocDelete()}
                  ></CancelIcon>
                </div>
              </div>
            )}
            {uploadError && (
              <>
                <div className='d-flex justify-content-center flex-column align-items-center progress-bar-screen'>
                  <div className='upload-progress-error p-3 d-flex justify-content-between'>
                    <div>{fileDetails?.name}</div>
                    <div className='me-5'>
                      {fileDetails?.size && bytesToSize(fileDetails?.size)}
                    </div>
                    <div className='d-flex justify-content-end ms-5'>
                      <div className=' upload-progress-title me-2'>UploadError</div>
                      <CancelIcon
                        className=' pointer me-1 '
                        onClick={(e) => onDocDelete()}
                      ></CancelIcon>
                    </div>
                  </div>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <h6 className='mt-3'>
                      Kindly ensure the document data meets the acceptance criteria and{' '}
                      <span className='error-text pointer' onClick={open}>
                        Try Again
                      </span>
                    </h6>
                  </div>
                </div>
              </>
            )}
            {!upload && !uploadError && (
              <div className='col-md-6 mt-4 mb-4 pb-5 px-4'>
                <div className='row ird-center-box text-left d-flex justify-content-around '>
                  <h6 className='mb-3'>Choose a mode</h6>

                  <div
                    className={`row d-flex justify-content-start align-items-center border border-dark ms-5
                    ${menuCreateOption === IRD_MENU_IMPORT ? 'menu-option-active' : 'menu-option'}
                  `}
                    onClick={() => setMenuCreateOption(IRD_MENU_IMPORT)}
                  >
                    <div className='col-2'>
                      <img
                        className='ps-3 icon'
                        src='/assets/icons/service-request/layer1.svg'
                        alt='icon'
                      />
                    </div>
                    <div className='col-9 d-flex flex-column mt-3'>
                      <h5>Import Menu</h5>
                      <p>Import the complete menu from POS</p>
                    </div>
                  </div>

                  <div
                    className={`row d-flex justify-content-start align-items-center border border-dark ms-5 mt-3
                  ${menuCreateOption === IRD_MENU_BUILD ? 'menu-option-active' : 'menu-option'}
                `}
                    onClick={() => setMenuCreateOption(IRD_MENU_BUILD)}
                  >
                    <div className='col-2 icon'>
                      <img
                        className='ps-2'
                        src='/assets/icons/service-request/015---Build-Blockchain.svg'
                        alt='icon'
                      />
                    </div>
                    <div className='col-9 d-flex flex-column mt-3'>
                      <h5>Build from scratch</h5>
                      <p>Build your menu, from items added to the library</p>
                    </div>
                  </div>

                  <div
                    className={`row d-flex justify-content-start align-items-center border border-dark ms-5 mt-3
                  ${menuCreateOption === IRD_MENU_UPLOAD ? 'menu-option-active' : 'menu-option'}
                `}
                    onClick={() => setMenuCreateOption(IRD_MENU_UPLOAD)}
                  >
                    <div className='col-2'>
                      <img
                        className='ps-3 icon'
                        src='/assets/icons/service-request/upload.svg'
                        alt='icon'
                      />
                    </div>
                    <div className='col-9 d-flex flex-column mt-3'>
                      <h5>Upload Menu</h5>
                      <p>Upload Menu document / Images from your local system</p>
                    </div>
                  </div>

                  {menuCreateOption === IRD_MENU_UPLOAD && (
                    <div className='position-relative d-flex justify-content-around w-100 ms-4'>
                      <div className='row d-flex justify-content-center mt-4 position-absolute'>
                        <div className='col-1'>
                          <InfoIcon fontSize='small' />
                        </div>
                        <div className='col-11 opacity-75'>
                          Document type: PDF, (Max size: 50MB) <br />
                          Image Type: PNG, JPG, JPEG, (Max size: 10MB)
                        </div>
                      </div>
                    </div>
                  )}
                  <div className='d-flex justify-content-center confirm-button'>
                    <Button
                      variant='outlined'
                      type='button'
                      color='secondary'
                      size='large'
                      className='me-4'
                      onClick={() => onViewToggle(false, null)}
                    >
                      Cancel
                    </Button>
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <Button
                        variant='contained'
                        color='primary'
                        className='me-3'
                        size='large'
                        onClick={onConfirm}
                        disabled={!menuCreateOption}
                      >
                        Confirm
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {isUploadDoc && (
        <MenuFileUpload
          groupId={selectedGroup}
          propertyType='hotels'
          createIrdMenu={createIrdMenu}
          onViewToggle={onViewToggle}
          hotelId={hotelId}
        />
      )}
      {menuModal && (
        <MenuModal
          menuModal={menuModal}
          setMenuModal={setMenuModal}
          onViewToggle={onViewToggle}
          posMenuList={posMenuList}
          setPosMenuList={setPosMenuList}
          hotelId={hotelId}
        />
      )}
    </div>
  );
};

export default InRoomDiningMenuCreateType;
